'use client'

import React, { useMemo, useState } from 'react'

type RangeKey = 'year' | '9m' | '6m'
type Point = { month: string; value: number }

const ALL: Point[] = [
  { month: 'Novembre 2025', value: 0 },
  { month: 'Décembre 20255', value: 1250 }, // ✅ was 290
  { month: 'Janvier 2026', value: 0 },
]

const clamp = (n: number, a: number, b: number) => Math.max(a, Math.min(b, n))

function buildSmoothPath(points: { x: number; y: number }[]) {
  if (points.length < 2) return ''
  const d: string[] = [`M ${points[0].x} ${points[0].y}`]
  for (let i = 1; i < points.length; i++) {
    const p0 = points[i - 1]
    const p1 = points[i]
    const cx = (p0.x + p1.x) / 2
    d.push(`C ${cx} ${p0.y}, ${cx} ${p1.y}, ${p1.x} ${p1.y}`)
  }
  return d.join(' ')
}

export default function MonthlyRegistrationsChart() {
  const [range, setRange] = useState<RangeKey>('year')

  const data = useMemo(() => ALL, [range])

  const W = 980
  const H = 280

  const pad = { left: 64, right: 64, top: 22, bottom: 54 }
  const chartW = W - pad.left - pad.right
  const chartH = H - pad.top - pad.bottom

  // ---- scale (✅ FIXED: dynamic yMax based on real peak)
  const yMin = 0
  const rawMax = Math.max(...data.map((d) => d.value), 1)
  const yMax = Math.ceil(rawMax / 50) * 50

  const round25 = (n: number) => Math.round(n / 25) * 25
  const grid = [yMax, round25(yMax * 0.833), round25(yMax * 0.533), 0]

  const xStep = data.length > 1 ? chartW / (data.length - 1) : chartW

  const innerTop = pad.top + 0
  const innerBottom = pad.top + chartH - 10
  const usableH = innerBottom - innerTop

  const points = data.map((p, i) => {
    const x = pad.left + i * xStep

    const isFirst = i === 0
    const isLast = i === data.length - 1
    const plotValue = isFirst || isLast ? 0 : p.value

    const t = (plotValue - yMin) / (yMax - yMin)
    const y = innerTop + (1 - clamp(t, 0, 1)) * usableH

    return { ...p, x, y }
  })

  const linePath = buildSmoothPath(points)
  const bottomY = pad.top + chartH
  const areaPath =
    points.length > 0
      ? `${linePath} L ${points[points.length - 1].x} ${bottomY} L ${points[0].x} ${bottomY} Z`
      : ''

  const startVerticalIdx = 0

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-xl font-semibold text-slate-900">Recensements par mois</h3>
          <p className="mt-1 text-sm text-slate-500">Overview de noús registrations de leur ebs avec</p>
        </div>

        <div className="flex-1 flex justify-center">
          <div className="text-2xl font-semibold text-slate-900">Janvier 2026</div>
        </div>

        <div className="flex items-center rounded-xl border border-slate-200 bg-slate-50 px-2 py-1">
          <button
            onClick={() => setRange('year')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              range === 'year' ? 'bg-slate-200 text-slate-800' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            Année
          </button>
          <button
            onClick={() => setRange('9m')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              range === '9m' ? 'bg-slate-200 text-slate-800' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            9 mois
          </button>
          <button
            onClick={() => setRange('6m')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              range === '6m' ? 'bg-slate-200 text-slate-800' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            6 mois
          </button>
        </div>
      </div>

      {/* Chart */}
      <div className="mt-5 rounded-2xl border border-slate-100 bg-white px-4 py-3">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[260px]" preserveAspectRatio="none">
          <defs>
            <linearGradient id="mwArea" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.14" />
              <stop offset="100%" stopColor="#60a5fa" stopOpacity="0" />
            </linearGradient>

            <linearGradient id="mwLine" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%" stopColor="#60a5fa" />
              <stop offset="100%" stopColor="#22c55e" />
            </linearGradient>
          </defs>

          {/* horizontal grid + left labels */}
          {grid.map((gv, idx) => {
            const t = idx / (grid.length - 1)
            const y = pad.top + t * chartH
            const isZero = gv === 0

            return (
              <g key={`g-${gv}`}>
                <line
                  x1={pad.left}
                  x2={pad.left + chartW}
                  y1={y}
                  y2={y}
                  stroke={isZero ? '#e2e8f0' : '#eef2f7'}
                  strokeWidth={isZero ? 1.2 : 1}
                />
                <text x={pad.left - 18} y={y + 4} textAnchor="end" fontSize={12} fill="#94a3b8" fontWeight={500}>
                  {gv}
                </text>
              </g>
            )
          })}

          {/* vertical guides */}
          {points.map((p, i) => {
            if (i < startVerticalIdx) return null
            return (
              <line
                key={`v-${i}`}
                x1={p.x}
                x2={p.x}
                y1={pad.top}
                y2={bottomY}
                stroke="#f1f5f9"
                strokeWidth={1}
              />
            )
          })}

          <path d={areaPath} fill="url(#mwArea)" />

          <path
            d={linePath}
            fill="none"
            stroke="url(#mwLine)"
            strokeWidth={3.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {points.map((p, i) => (
            <g key={`p-${i}`}>
              <circle cx={p.x} cy={p.y} r={6.5} fill="white" stroke="#16a34a" strokeWidth={2.5} />
            </g>
          ))}

          {/* ✅ REMOVED: right-side labels */}
          {/* ✅ REMOVED: x-axis numbers under the axis */}

          {/* months */}
          {points.map((p, i) => (
            <text key={`m-${i}`} x={p.x} y={bottomY + 42} textAnchor="middle" fontSize={12} fill="#94a3b8" fontWeight={500}>
              {p.month}
            </text>
          ))}
        </svg>
      </div>
    </div>
  )
}
